import { Modal, Form, Input, InputNumber, Select, Button, Upload, message, Tag } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";

const EditCourseModal = ({ visible, onCancel, onOk, categories, form: externalForm, initialValues }) => {
  const [form] = Form.useForm();
  const [imageFile, setImageFile] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (visible && initialValues) {
      form.setFieldsValue({
        name: initialValues.name,
        slug: initialValues.slug,
        description: initialValues.description,
        category: initialValues.category._id,
        price: initialValues.price,
        status: initialValues.isActive ? 'active' : 'inactive',
        author: initialValues.author
      });
    }
  }, [visible, initialValues, form]);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setImageFile(null);
    }
  }, [visible, form]);

  const handleImageChange = ({ file }) => {
    if (!file) {
      setImageFile(null);
      return;
    }

    const currentFile = file.originFileObj || file;

    // Validate file type
    const isImage = currentFile.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return;
    }
    
    // Validate file size (2MB)
    const isLt2M = currentFile.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB!');
      return;
    }

    setImageFile(currentFile);
  };

  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);
      
      // Validate form
      const values = await form.validateFields();
      
      // Create FormData object
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('description', values.description || '');
      formData.append('category', values.category);
      formData.append('price', values.price);
      formData.append('status', values.status);
      formData.append('author', values.author || '');
      
      // Only append image if a new one is selected
      if (imageFile) {
        formData.append('image', imageFile);
      }

      // Call parent's onOk with formData
      await onOk(formData);
      
      // Reset form
      form.resetFields();
      setImageFile(null);
      
      // Close modal
      onCancel();
    } catch (error) {
      console.error('Error updating course:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.message) {
        message.error(error.message);
      } else {
        message.error('Failed to update course');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Modal
      title="Edit Course"
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      okText="Save"
      confirmLoading={submitLoading}
    >
      <Form 
        form={form} 
        layout="vertical"
      >
        <Form.Item 
          label="Course Name" 
          name="name" 
          rules={[
            { required: true, message: 'Course name is required' },
            { max: 255, message: 'Course name must not exceed 255 characters' },
            { min: 1, message: 'Course name cannot be empty' },
            { whitespace: true, message: 'Course name cannot be only whitespace' }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item 
          label="URL-Friendly" 
          name="slug"
        >
          <Input disabled placeholder="Slug cannot be modified after creation" />
        </Form.Item>

        <Form.Item 
          label="Author" 
          name="author" 
          rules={[
            { required: true, message: 'Author is required' },
            { max: 255, message: 'Author name must not exceed 255 characters' },
            { whitespace: true, message: 'Author name cannot be only whitespace' }
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item 
          label="Price" 
          name="price" 
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              validator: async (_, value) => {
                if (value === null || value === undefined || value === '') {
                  throw new Error('Price is required');
                }
                if (typeof value === 'string') {
                  throw new Error('Price cannot contain letters or special characters');
                }
                if (typeof value !== 'number' || isNaN(value)) {
                  throw new Error('Price must be a valid number');
                }
                if (value <= 0) {
                  throw new Error('Price must be greater than 0');
                }
                if (value > 500) {
                  throw new Error('Price must not exceed 500$');
                }
              }
            }
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0.01}
            precision={2}
            step={0.01}
            placeholder="Enter price"
            formatter={(value) => {
              if (!value) return '';
              return `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            }}
            parser={(value) => {
              const parsed = value.replace(/\$\s?|(,*)/g, '');
              if (parsed === '') return null;
              const num = parseFloat(parsed);
              return isNaN(num) ? null : num;
            }}
            onKeyPress={(e) => {
              const charCode = e.which ? e.which : e.keyCode;
              if (
                charCode !== 46 && // dấu chấm
                charCode > 31 && 
                (charCode < 48 || charCode > 57) // không phải số
              ) {
                e.preventDefault();
                message.error('Please enter numbers only. Letters and special characters are not allowed in price field.');
                return false;
              }
              return true;
            }}
          />
        </Form.Item>

        <Form.Item 
          label="Category" 
          name="category" 
          rules={[
            { required: true, message: 'Category is required' }
          ]}
        >
          <Select>
            {categories.map((cat) => (
              <Select.Option key={cat._id} value={cat._id}>
                {cat.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item 
          label="Description" 
          name="description"
          rules={[
            { max: 500, message: 'Description must not exceed 500 characters' }
          ]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item 
          name="status" 
          label="Status"
          rules={[
            { required: true, message: 'Status is required' }
          ]}
        >
          <Select>
            <Select.Option value="active">
              <Tag color="success">Active</Tag>
            </Select.Option>
            <Select.Option value="inactive">
              <Tag color="error">Inactive</Tag>
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Course Image"
          name="image"
          extra={initialValues?.imageUrl && !imageFile && (
            <img 
              src={initialValues.imageUrl} 
              alt="Current course image" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '150px', 
                marginTop: '8px',
                borderRadius: '4px'
              }} 
            />
          )}
        >
          <Upload
            listType="picture"
            maxCount={1}
            beforeUpload={() => false}
            onChange={handleImageChange}
            accept="image/*"
            onRemove={() => {
              setImageFile(null);
              return true;
            }}
          >
            <Button icon={<UploadOutlined />}>Change Image</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditCourseModal;
